import type { TFunction } from 'i18next';
import type { Delivery, InventoryLevel, SiteLogistics } from '../interfaces/ILogistics.js';
import { fold } from '../utils/text.js';
import { buildPickPrefill, type PickPrefill } from './orderPrefill.js';
import { unsyncedOrderNoticeByName, type UnsyncedOrderNotice } from './oneOpenOrder.js';
import type { StockUnitOf } from '../../logistics/index.js';

/**
 * The lookups every host of the order form wires from the site's logistics
 * payload — the Logistics page's dialog and detail drawer, and the Scheduler
 * Stock tab's "Create delivery" modal. One place, so a fourth host cannot drift
 * on which fold, which exclusion, or which "still has cover" rule applies.
 */

/** This material's running stock, when it has one that still has cover — the
 *  only case a merge-on-arrival makes sense. Same NFKC+lowercase fold as every
 *  other delivery-material link. */
export function openStockFor(inventory: InventoryLevel[], itemName: string | undefined): InventoryLevel | null {
  if (!itemName) return null;
  const level = levelFor(inventory, itemName);
  return level && !level.needsCheck ? level : null;
}

/** The material's stock record, needing a check or not — the one folded-name
 *  lookup every "which level is this material?" question goes through. */
export function levelFor(inventory: InventoryLevel[], itemName: string): InventoryLevel | null {
  const key = fold(itemName.trim());
  return inventory.find((level) => fold(level.materialName.trim()) === key) ?? null;
}

/** The unit each material is counted in on site, whether or not it still has
 *  cover — a delivery line for it uses that unit (`draftsFromInitial`, and the
 *  server's `unit-not-stock-unit`). */
export function stockUnitFor(inventory: InventoryLevel[]): StockUnitOf {
  return (itemName) => levelFor(inventory, itemName)?.unit ?? null;
}

/** Picking an existing material fills its habitual fields and locks the unit
 *  — derived from the same deliveries/levels the payload carries. */
export function materialPrefillFor(data: SiteLogistics, tz: string, t: TFunction): (name: string) => PickPrefill {
  return (name) => buildPickPrefill(name, data.deliveries, data.inventory, tz, t);
}

/** One-unsynced-order rule: the notice for a name that already has an
 *  unsynced order in flight. `excludeRef` leaves out the order being edited. */
export function openOrderNoticeFor(
  deliveries: Delivery[],
  excludeRef?: string,
): (name: string) => UnsyncedOrderNotice | null {
  return (name) => unsyncedOrderNoticeByName(deliveries, name, excludeRef);
}
